import { User, UserService } from '@gbhem/api';
import { Route, Switch, useParams, useRouteMatch } from 'react-router-dom';

import {
  DocumentsTable,
  ProtectedRoute,
  SubNavbar,
  SubNavLink,
  UserEmailVerification,
  UserProfile,
  UsersTable
} from '../../components';
import { UserFormsTable } from '../../components/FormsTable';
import { UserAssignments } from '../../components/UserAssignments';
import { Can, useAuthenticatedUserAbility } from '../../providers/PermissionProvider';

type AppAbility = ReturnType<typeof useAuthenticatedUserAbility>;

/**
 * Permission check for viewing a specific user profile
 */
async function canViewUserProfile(
  ability: AppAbility,
  authUser: User,
  params: Record<string, string | undefined>
): Promise<boolean> {
  // Route param should always be present, but check just in case
  if (!params.id) return false;

  // Always allow viewing own profile
  if (authUser.id === params.id) return true;

  // Check basic read permission
  if (!ability.can('read', 'User')) return false;

  // Verify this specific user is viewable
  const { canView } = await UserService.canViewUser(params.id);
  return canView;
}

/**
 * Permission check for user sub-routes (documents, forms, assignments)
 * Requires update permission on User entity
 */
async function canAccessUserSubRoute(
  ability: AppAbility,
  authUser: User,
  params: Record<string, string | undefined>
): Promise<boolean> {
  // Route param should always be present, but check just in case
  if (!params.id) return false;

  // Allow viewing own data
  if (authUser.id === params.id) return true;

  // Check update permission (matches Can component)
  if (!ability.can('update', 'User')) return false;

  // Verify this specific user is viewable
  const { canView } = await UserService.canViewUser(params.id);
  return canView;
}

export function Users() {
  const { url } = useRouteMatch();

  return (
    <>
      <Switch>
        {/* Users list - requires read permission on User */}
        <ProtectedRoute exact path={url} hasPermission={(ability) => ability.can('read', 'User')}>
          <UsersTable />
        </ProtectedRoute>

        {/* Email verification - public route accessible via email link */}
        <ProtectedRoute path={`${url}/email-verification`}>
          <UserEmailVerification />
        </ProtectedRoute>

        {/* Individual user profile - check if user can view this specific user */}
        <ProtectedRoute path={`${url}/:id`} hasPermission={canViewUserProfile}>
          <UserPage />
        </ProtectedRoute>
      </Switch>
    </>
  );
}

function UserPage() {
  const { url, path } = useRouteMatch();
  const { id } = useParams<{ id: string }>();

  return (
    <>
      <SubNavbar>
        <SubNavLink exact to={url}>
          Profile
        </SubNavLink>
        <Can do="update" on="User">
          <SubNavLink to={`${url}/documents`}>Documents</SubNavLink>
          {/* TODO: if desired, block participants from seeing this tab for themselves */}
          <SubNavLink to={`${url}/user-assignments`}>User Assignments</SubNavLink>
          <SubNavLink to={`${url}/forms`}>Forms</SubNavLink>
        </Can>
      </SubNavbar>
      <Switch>
        {/* User assignments - requires update permission + user visibility */}
        <ProtectedRoute path={`${path}/user-assignments`} hasPermission={canAccessUserSubRoute}>
          <UserAssignments userId={id} />
        </ProtectedRoute>

        {/* User documents - requires update permission + user visibility */}
        <ProtectedRoute path={`${path}/documents`} hasPermission={canAccessUserSubRoute}>
          <DocumentsTable userId={id} />
        </ProtectedRoute>

        {/* User forms - requires update permission + user visibility */}
        <ProtectedRoute path={`${path}/forms`} hasPermission={canAccessUserSubRoute}>
          <UserFormsTable userId={id} />
        </ProtectedRoute>

        {/* User profile view - uses parent route's permission check */}
        <Route>
          <UserProfile />
        </Route>
      </Switch>
    </>
  );
}

export default Users;
